import React, { useEffect } from "react";
import { Button } from "components/components/atoms";
import { Container } from "components/shared";
import styled, { css } from "styled-components";
import tw from "tailwind.macro";
import { getFieldValue } from "@sitecore-jss/sitecore-jss-react";
import useBreakpoint from "utils/hooks/useBreakpoint";
import breakpoints from "utils/breakpoints";
import cx from "classnames";
import trackingEvents from "utils/trackingEvents";
import { track } from "utils/tracking";
import { useLocation } from "react-router-dom";
import { scrollToSection } from "utils/scrollTo";
import { useUiValue } from "stores/uiContext";

const Heading = styled.h2`
  @media ${breakpoints.lg} {
    font-size: 2.25rem;
  }
  @media ${breakpoints.xl} {
    font-size: 2.875rem;
  }
`;

const boxShadow = css`
  box-shadow: 0px 10px 40px rgba(0, 0, 0, 0.2);
`;

const StyledDiv = styled.div`
  transition: box-shadow 0.2s ease-in-out;
  ${({ shadowed }) => shadowed && boxShadow}
  ${({ shadowedOnHover }) =>
    shadowedOnHover &&
    `
    border-radius: 0.625rem;
    &:hover {
      border-color: white;
      ${boxShadow}
    }
  `}
`;

const StyledButton = styled(Button)`
  @media ${breakpoints.md} {
    ${tw`px-4 h-auto`}
  }
  @media ${breakpoints.lg} {
    width: 12.7rem;
    ${tw`px-0`}
  }
`;

const getButtonColor = (textStyle, filled) => {
  if (textStyle === "blue-white") {
    return "action-blue";
  }

  if (textStyle === "blue-blue") {
    return "mattamy-blue";
  }

  if (!filled && textStyle === "white-blue") {
    return "white";
  }

  if (filled && textStyle === "white-blue") {
    return "mattamy-blue";
  }
};

const ScheduleAppointmentCTA = ({ fields, rendering }) => {
  let componentName;

  if (rendering) {
    componentName = rendering.componentName;
  }

  const title = getFieldValue(fields, "title");
  const firstCtaLink = getFieldValue(fields, "firstCtaLink") || {};
  const secondCtaLink = getFieldValue(fields, "secondCtaLink") || {};
  const textStyle = getFieldValue(fields, "textStyle") || "blue-white";

  const breakpoint = useBreakpoint(
    typeof window !== "undefined" && window.innerWidth
  );
  const isMobile = ["xs", "sm"].includes(breakpoint);
  const [{ notificationHeight, nationalNotificationHeight }] = useUiValue();
  const location = useLocation();
  const hash = location.hash;
  useEffect(() => {
    if (hash && hash.substring(1) === "ScheduleAppointmentCTA")
      setTimeout(() => {
        scrollToSection(
          hash.substring(1),
          100 + notificationHeight + nationalNotificationHeight
        );
      }, 1500);
  });
  if (!firstCtaLink.text && !secondCtaLink.text) {
    return null;
  }

  const trackEventId = trackingEvents.BookAppointment;

  return (
    <>
      <section
        className="flex flex-col items-center justify-start py-10 w-screen"
        id={componentName}
        tabIndex="-1"
      >
        <Container>
          <StyledDiv
            shadowed={textStyle === "blue-white"}
            shadowedOnHover={false}
            className={cx(
              "flex flex-col md:flex-row items-center justify-between overflow-visible ",
              "pl-30-mattamy pr-8 rounded-lg md:pl-20 md:py-4 py-10 lg:py-8",
              {
                "text-mattamy-blue bg-white": textStyle === "blue-white",
                "text-mattamy-blue bg-light-blue": textStyle === "blue-blue",
                "text-white bg-mattamy-blue": textStyle === "white-blue",
              }
            )}
          >
            <Heading
              className={cx(
                "font-bold font-trade-gothic-20 text-center md:text-left",
                "md:mt-4 mb-5 md:mb-2 md:mr-8 leading-tight text-3xl mb-1 md:mb-2"
              )}
            >
              {title}
            </Heading>
            <div className="w-full md:w-auto flex flex-col md:flex-row md:flex-wrap items-center justify-center md:justify-end">
              {firstCtaLink.text && (
                <div
                  className={cx("w-full md:w-auto", {
                    "md:mr-8 lg:mr-10": secondCtaLink.text,
                  })}
                >
                  <StyledButton
                    className={cx({
                      "hover:bg-action-blue hover:text-white":
                        !firstCtaLink.primary && textStyle === "blue-white",
                      "hover:bg-mattamy-blue-dark hover:text-white hover:border-mattamy-blue-dark":
                        !firstCtaLink.primary &&
                        ["blue-blue", "white-blue"].includes(textStyle),
                    })}
                    color={getButtonColor(textStyle)}
                    variant={
                      firstCtaLink.primary || isMobile ? "filled" : "outlined"
                    }
                    textColor={getButtonColor(textStyle, isMobile)}
                    fullWidthMobile
                    onClick={() => {
                      window.dataLayer.push({ event: "schedule-appointment" });
                      if (trackEventId) {
                        track({ eventId: trackEventId });
                      }
                      window.Calendly.initPopupWidget({
                        url: firstCtaLink.href,
                      });
                    }}
                  >
                    {firstCtaLink.text}
                  </StyledButton>
                </div>
              )}
              {secondCtaLink.text && (
                <StyledButton
                  className={cx("mt-5 md:mt-0", {
                    "hover:bg-action-blue hover:text-white":
                      !secondCtaLink.primary && textStyle === "blue-white",
                    "hover:bg-mattamy-blue-dark hover:text-white hover:border-mattamy-blue-dark":
                      !secondCtaLink.primary &&
                      ["blue-blue", "white-blue"].includes(textStyle),
                  })}
                  color={getButtonColor(textStyle)}
                  variant={
                    secondCtaLink.primary || isMobile ? "filled" : "outlined"
                  }
                  textColor={getButtonColor(textStyle, isMobile)}
                  fullWidthMobile
                  onClick={() => {
                    window.dataLayer.push({
                      event: "schedule-appointment-virtual",
                    });
                    if (trackEventId) {
                      track({ eventId: trackEventId });
                    }
                    window.Calendly.initPopupWidget({
                      url: secondCtaLink.href,
                    });
                  }}
                >
                  {secondCtaLink.text}
                </StyledButton>
              )}
            </div>
          </StyledDiv>
        </Container>
      </section>
    </>
  );
};

export default ScheduleAppointmentCTA;
